import { useCallback, useEffect, useRef, useState } from "react";
import { KdsTableViewUpdate } from "../../../config/Services";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import { assertKitchenResponse } from "./kitchenActions";
import { tablePendingJobs } from "./tableWorkload";

const text = (value) => value == null ? "" : String(value).trim();

export default function useKdsTableActions({
  enabled, tables, locationId, updatedBy, updatedByName,
  allowAccept = true, allowReject = true, onUpdated, onAcknowledged,
}) {
  const showSnackbar = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState(null);
  const [error, setError] = useState("");
  const locked = useRef(false);
  const submitted = useRef({ tables: null, keys: new Set() });
  const latest = useRef(null);
  const mounted = useRef(true);
  latest.current = { enabled, tables, locationId, updatedBy, updatedByName,
    allowAccept, allowReject, onUpdated, onAcknowledged };
  useEffect(() => { setAction(null); setError(""); }, [enabled, locationId]);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const run = useCallback(async (jobIds, selectedTable, status) => {
    const failureMessage = status === "REJECTED" ? "Unable to Reject the Order" : "Unable to accept the order";
    if (locked.current) throw new Error("A table update is already in progress.");
    const scope = latest.current;
    const isCurrent = () => mounted.current && latest.current?.enabled &&
      latest.current?.locationId === scope?.locationId && latest.current?.updatedBy === scope?.updatedBy;
    locked.current = true;
    setBusy(true);
    setError("");
    setAction({ tableKey: selectedTable?.key, status });
    let attempted = false;
    let failure = null;
    try {
      if (!scope?.enabled || !scope?.locationId) throw new Error("Table updates are not available.");
      if ((status === "IN_PROGRESS" && !scope?.allowAccept) ||
        (status === "REJECTED" && !scope?.allowReject)) throw new Error("This table action is disabled.");
      const table = scope?.tables?.find((candidate) => candidate?.key === selectedTable?.key);
      const pending = tablePendingJobs(table);
      const ids = [...new Set((jobIds || []).filter(Boolean).map(String))];
      if (!table || !pending.length || !ids.length ||
        ids.some((id) => !pending.some((job) => String(job?.jobId) === id))) {
        throw new Error("These pending orders have changed. Refresh and select the table again.");
      }
      const identities = pending.map((job) => ({
        tableId: text(job?.metadata?.tableId ?? table?.cartTableId),
        tableNumber: text(job?.metadata?.tableNumber ?? table?.name),
      }));
      const { tableId, tableNumber } = identities[0];
      if (!tableId || !tableNumber || identities.some((identity) =>
        identity.tableId !== tableId || identity.tableNumber !== tableNumber)) {
        throw new Error("The table identity could not be confirmed. Refresh before trying again.");
      }
      if (submitted.current.tables !== scope.tables) submitted.current = { tables: scope.tables, keys: new Set() };
      if (submitted.current.keys.has(tableId)) throw new Error("This table was already submitted. Wait for the refreshed orders.");
      submitted.current.keys.add(tableId);
      attempted = true;
      const response = await KdsTableViewUpdate({
        tableId, tableNumber, status, locationId: scope.locationId,
        updatedBy: scope.updatedBy, updatedByName: scope.updatedByName,
      });
      assertKitchenResponse(response);
      const envelopes = [response?.data, response?.data?.data, response?.data?.data?.data, response];
      const result = envelopes.find((value) => value?.targetStatus != null);
      const requested = Number(result?.requestedQuantity), updated = Number(result?.updatedQuantity);
      if (Number(response?.status) >= 400 || envelopes.some((value) => Number(value?.statusCode) >= 400) ||
        !result || Number(result?.es) > 0 || result?.success === false ||
        ["ERROR", "FAILED", "FAILURE"].includes(result?.status) ||
        text(result?.tableId) !== tableId || text(result?.tableNumber) !== tableNumber ||
        result?.targetStatus !== status || result?.requestedQuantity == null || !Number.isFinite(requested) ||
        requested <= 0 || result?.updatedQuantity == null || !Number.isFinite(updated) || updated < 0 || updated > requested) {
        throw new Error("The table update could not be confirmed. Refresh before trying again.");
      }
      if (isCurrent() && updated > 0 && Array.isArray(result?.affectedJobIds) && result.affectedJobIds.length) {
        latest.current.onAcknowledged?.(result.affectedJobIds);
      }
      if (updated !== requested) {
        throw new Error(`Only ${updated} of ${requested} portions were updated. Review the refreshed table before trying again.`);
      }
    } catch (err) {
      failure = err instanceof Error ? err : new Error(failureMessage);
    } finally {
      if (attempted && isCurrent()) {
        try { await latest.current.onUpdated?.(); }
        catch (_) { failure = failure || new Error("The table was updated, but its orders could not refresh. Refresh before trying again."); }
      }
      locked.current = false;
      setBusy(false);
    }
    if (failure) {
      if (isCurrent()) {
        // Backend messages may be objects/arrays: never render them in React or the toast.
        setError(failureMessage);
        showSnackbar?.(failureMessage, "error");
      }
      throw failure;
    }
    if (isCurrent()) showSnackbar?.(status === "REJECTED" ? "Pending table orders rejected" : "Pending table orders moved to processing", "success");
  }, [showSnackbar]);

  const accept = useCallback((ids, table) => run(ids, table, "IN_PROGRESS"), [run]);
  const reject = useCallback((ids, table) => run(ids, table, "REJECTED"), [run]);
  return { accept, reject, busy, action, error };
}
